<template>
  <article
    class="gw-card"
    :class="{
      'gw-card--selected': selected,
      'gw-card--used': item.used,
    }"
    @click="$emit('toggle-expand', item)"
  >
    <div class="gw-card__media">
      <img
        v-if="item.preview && !imageFailed"
        :src="item.preview"
        alt=""
        class="gw-card__img"
        @error="imageFailed = true"
      />
      <div v-else class="gw-card__img gw-card__img--placeholder">
        {{ initial }}
      </div>

      <!-- Pas de pastille sur une vidéo : elle est hors scoring. -->
      <score-pill
        v-if="item.score !== null"
        class="gw-card__score"
        variant="badge"
        :score="item.score"
        :notes="item.notes"
      />

      <span v-if="item.isVideo" class="gw-card__play">
        <v-icon size="34" color="#FFFFFF">{{ icons.mdiPlayCircle }}</v-icon>
      </span>

      <!-- Ticking must not toggle the detail panel. -->
      <input
        type="checkbox"
        class="gw-card__checkbox"
        :checked="selected"
        :disabled="item.used"
        @click.stop
        @change="$emit('toggle-select', item)"
      />
    </div>

    <div class="gw-card__body">
      <h3 class="gw-card__title" :title="item.title">{{ item.title }}</h3>

      <div class="gw-card__meta">
        <source-type-badge :source-name="item.sourceName" size="xs" />
        <span class="gw-card__domain">{{ item.domain || item.originLabel }}</span>
        <span class="gw-card__date">{{ relativeDate }}</span>
        <span class="gw-card__caret">
          <v-icon size="12" color="#94A3B8">
            {{ expanded ? icons.mdiChevronUp : icons.mdiChevronDown }}
          </v-icon>
        </span>
      </div>

      <source-detail-panel
        v-if="expanded"
        :item="item"
        layout="card"
        @click.native.stop
        @open-video="$emit('open-video', $event)"
      />
    </div>
  </article>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import { mdiChevronDown, mdiChevronUp, mdiPlayCircle } from "@mdi/js";

import ScorePill from "./ScorePill.vue";
import SourceDetailPanel from "./SourceDetailPanel.vue";
import SourceTypeBadge from "./SourceTypeBadge.vue";
import { formatRelativeDate } from "../source-presentation";

export default {
  name: "SourceCard",
  components: {
    ScorePill,
    SourceDetailPanel,
    SourceTypeBadge,
  },
  props: {
    item: { type: Object, required: true },
    selected: { type: Boolean, default: false },
    expanded: { type: Boolean, default: false },
  },
  setup(props) {
    return {
      icons: { mdiChevronDown, mdiChevronUp, mdiPlayCircle },
      imageFailed: ref(false),
      initial: computed(() =>
        (props.item.domain || props.item.title || "?").charAt(0).toUpperCase()
      ),
      relativeDate: computed(() =>
        formatRelativeDate(props.item.publicationDate)
      ),
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../_global-watch.scss";

.gw-card {
  display: flex;
  flex-direction: column;
  border: 1.5px solid $rmr-border-hover;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-0;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    box-shadow: $rmr-shadow-card-hover;
  }
}

.gw-card--selected {
  border-color: $rmr-indigo;
}

.gw-card--used {
  opacity: 0.55;
}

.gw-card__media {
  position: relative;
}

.gw-card__img {
  display: block;
  width: 100%;
  height: 140px;
  object-fit: cover;
  background: $rmr-surface-2;
}

.gw-card__img--placeholder {
  font-size: 28px;
  @include gw-thumb-placeholder;
}

.gw-card__score {
  position: absolute;
  top: 10px;
  right: 10px;
}

// La miniature seule ne dit pas qu'il s'agit d'une vidéo.
.gw-card__play {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
  filter: drop-shadow(0 1px 6px rgba(15, 23, 42, 0.5));
}

.gw-card__checkbox {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 18px;
  height: 18px;
  accent-color: $rmr-indigo;
  cursor: pointer;
}

.gw-card__body {
  display: flex;
  flex-direction: column;
  padding: 12px 14px 14px;
}

.gw-card__title {
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.35;
  color: $rmr-text-base;
  min-height: 54px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gw-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  font-size: 11.5px;
  color: $rmr-text-muted;
}

.gw-card__domain {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 45%;
}

.gw-card__date {
  color: $rmr-text-subtle;
}

.gw-card__caret {
  margin-left: auto;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-pill;
  background: $rmr-surface-0;
}
</style>
